import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiFetch } from "@/lib/api";
import type {
  BachecaMessaggio,
  BachecaStato,
  BachecaTipo,
  BachecaVoce,
  ParteResponsabile,
} from "@/types/api";

interface UseBachecaOptions {
  tipo?: BachecaTipo;
  stato?: BachecaStato;
  parte_responsabile?: ParteResponsabile;
}

export function useBacheca(opts: UseBachecaOptions = {}) {
  const params = new URLSearchParams();
  if (opts.tipo) params.set("tipo", opts.tipo);
  if (opts.stato) params.set("stato", opts.stato);
  if (opts.parte_responsabile) params.set("parte_responsabile", opts.parte_responsabile);
  const qs = params.toString();

  return useQuery({
    queryKey: ["bacheca", opts],
    queryFn: () => apiFetch<BachecaVoce[]>(`/api/v1/bacheca${qs ? `?${qs}` : ""}`),
    staleTime: 30_000,
  });
}

export function useBachecaMessaggi(voceId: string | null) {
  return useQuery({
    queryKey: ["bacheca-messaggi", voceId],
    queryFn: () =>
      apiFetch<BachecaMessaggio[]>(`/api/v1/bacheca/${voceId}/messaggi`),
    enabled: !!voceId,
    staleTime: 10_000,
  });
}

interface AggiungiMessaggioArgs {
  voceId: string;
  corpo: string;
}

/**
 * Aggiunge un messaggio al thread. Usato sia dal cliente sia da Maitime
 * (la `parte_autore` è dedotta backend-side dall'utente corrente). NON chiude
 * la voce: per chiudere c'è l'endpoint admin.
 */
export function useAggiungiMessaggio() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ voceId, corpo }: AggiungiMessaggioArgs) =>
      apiFetch<BachecaMessaggio>(`/api/v1/bacheca/${voceId}/messaggi`, {
        method: "POST",
        body: JSON.stringify({ corpo }),
      }),
    onSuccess: (_msg, vars) => {
      qc.invalidateQueries({ queryKey: ["bacheca"] });
      qc.invalidateQueries({ queryKey: ["bacheca-messaggi", vars.voceId] });
      qc.invalidateQueries({ queryKey: ["avanzamento"] });
    },
  });
}

interface CreaSollecitoArgs {
  titolo: string;
  corpo?: string;
  destinatario_ruolo: string;
  carico_ids?: string[];
}

/**
 * Crea un sollecito interno (voce SOLLECITO assegnata a Marina/Fabio) — l'azione
 * «Inoltra» del Cruscotto Direzionale (M4). Riservata a Presidenza/Management/staff
 * lato backend. NB: «Vedi come» è read-only → testabile solo con login reale.
 */
export function useCreaSollecito() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (args: CreaSollecitoArgs) =>
      apiFetch<BachecaVoce>("/api/v1/bacheca", {
        method: "POST",
        body: JSON.stringify(args),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["bacheca"] });
    },
  });
}
